import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react'
import { RequestContext } from '../../../../context/RequestContext'
import { criteriaSentence } from '../../../../js/criteria-phrase'
import { UserContext } from '../../../../context/UserContext'
import { LocationContext } from '../../../../context/LocationContext'
import { getMarketByName } from '../../../../utils/markets'
import { dismissModal } from '../../../../service/modal-service'
import { motion, AnimatePresence } from 'framer-motion'
import { 
    ChevronLeft, 
    ChevronRight, 
    Target, 
    Scale, 
    User, 
    MapPin, 
    Clock, 
    Calendar, 
    AlertCircle,
    Dumbbell,
    Home,
    Building2,
    TreePine,
    Warehouse,
    Sun,
    Sunrise,
    Sunset,
    Moon,
    ShieldCheck
} from 'lucide-react'
import { HiSparkles } from 'react-icons/hi'

// Question configuration with icons and visual enhancements
const questionConfig = {
    "1": {
        icon: Target,
        title: "Fitness Goal",
        subtitle: "What is your fitness goal?",
        useGrid: false,
        choiceIcons: {
            "Lose Fat and Build Muscle": { emoji: "💪", color: "from-red-500 to-orange-500" },
            "Lose Weight": { emoji: "⚖️", color: "from-blue-500 to-cyan-500" },
            "Build Muscle": { emoji: "🏋️", color: "from-purple-500 to-pink-500" },
            // "Functional Fitness": { emoji: "🎯", color: "from-green-500 to-emerald-500" },
            "Prenatal / Postnatal": { emoji: "🤰", color: "from-pink-400 to-rose-400" }
            // "Elderly Fitness": { emoji: "🧓", color: "from-yellow-400 to-amber-400"}            
        }
    },
    "2": {
        icon: Scale,
        title: "Body Weight",
        subtitle: "What is your current body weight?",
        useGrid: true,
        choiceIcons: {
            "Not Sure": { emoji: "🤔", color: "from-gray-400 to-gray-500" },
            "<50kg": { emoji: "🪶", color: "from-green-400 to-emerald-400" },
            "50-60": { emoji: "👤", color: "from-teal-400 to-cyan-400" },
            "61-70": { emoji: "👤", color: "from-blue-400 to-indigo-400" },
            "71-80": { emoji: "👤", color: "from-indigo-400 to-purple-400" },
            "81-90": { emoji: "👤", color: "from-purple-400 to-pink-400" },
            "91-100": { emoji: "👤", color: "from-pink-400 to-rose-400" },
            ">100kg": { emoji: "💪", color: "from-rose-400 to-red-400" }
        }
    },
    "4": {
        icon: User,
        title: "Trainer Preference",
        subtitle: "Gender preference for your trainer? (subject to availability)",
        useGrid: false,
        choiceIcons: {
            "No Preference": { emoji: "👥", color: "from-gray-500 to-slate-500", note: "best recommended" },
            "Male": { emoji: "👨‍🏫", color: "from-blue-500 to-indigo-500", note: "fast matching" },
            "Female": { emoji: "👩‍🏫", color: "from-pink-500 to-rose-500", note: "limited & extra $20/session" }
        }
    },
    "5": {
        icon: MapPin,
        title: "Training Location",
        subtitle: "Where would you like to train?",
        useGrid: false,
        choiceIcons: {
            "Home": { emoji: "🏠", color: "from-amber-500 to-orange-500", Icon: Home },
            "Condo Gym": { emoji: "🏢", color: "from-blue-500 to-cyan-500", Icon: Building2 },
            "Outdoor": { emoji: "🌳", color: "from-green-500 to-emerald-500", Icon: TreePine },
            "Public Gym": { emoji: "🏋️", color: "from-purple-500 to-pink-500", Icon: Dumbbell }
        }
    },
    "6": {
        icon: Clock,
        title: "Workout Time",
        subtitle: "When do you prefer to exercise?",
        useGrid: true,
        choiceIcons: {
            "7am-9am": { emoji: "🌅", color: "from-orange-400 to-amber-400", Icon: Sunrise },
            "9am-12pm": { emoji: "☀️", color: "from-yellow-400 to-orange-400", Icon: Sun },
            "12pm-3pm": { emoji: "🌞", color: "from-amber-500 to-yellow-500", Icon: Sun },
            "3pm-6pm": { emoji: "🌤️", color: "from-orange-500 to-red-400", Icon: Sunset },
            "6pm-9pm": { emoji: "🌆", color: "from-purple-500 to-indigo-500", Icon: Moon },
            "Flexible": { emoji: "⏰", color: "from-gray-500 to-slate-500", Icon: Clock }
        }
    },
    "7": {
        icon: Calendar,
        title: "When to Start?",
        subtitle: "When are you ready to begin?",
        useGrid: false,
        choiceIcons: {
            "Ready to Start Immediately": { emoji: "🚀", color: "from-green-500 to-emerald-500" },
            "In Few Weeks": { emoji: "📅", color: "from-blue-500 to-cyan-500" },
            "Discuss First": { emoji: "💬", color: "from-purple-500 to-pink-500" },
            "Just Exploring": { emoji: "🔍", color: "from-gray-500 to-slate-500" }
        }
    },
    "8": {
        icon: AlertCircle,
        title: "Health Information",
        subtitle: "Any injuries or physical constraints?",
        isOpenText: true
    }
}

// Animation variants - simplified to prevent layout shifts
const pageVariants = {
    initial: {
        opacity: 0
    },
    animate: {
        opacity: 1,
        transition: {
            duration: 0.2
        }
    },
    exit: {
        opacity: 0,
        transition: {
            duration: 0.15
        }
    }
}

// Compact Selection Card Component for Grid Layout
const CompactCard = ({ choice, config, isSelected, onClick }) => {
    const choiceConfig = config?.choiceIcons?.[choice.value] || {}
    const { emoji, color } = choiceConfig
    // choice.note: market note from utils/market-labels (the female premium in AED / RM)
    const note = choice.note ?? choiceConfig.note
    
    return (
        <div
            onClick={onClick}
            className={`
                relative py-2 rounded-xl cursor-pointer select-none
                transition-all duration-150 ease-out
                ${isSelected 
                    ? 'bg-linear-to-br from-orange-50 to-amber-50 border-2 border-orange-400 shadow-md' 
                    : 'bg-white border-2 border-gray-200 hover:border-orange-200 hover:shadow-sm'
                }
            `}
        >
            <div className="flex flex-col items-center text-center">
                {/* Icon/Emoji */}
                <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center text-xl
                `}>
                    {emoji || "📌"}
                </div>
                
                {/* Text */}
                <span className={`
                    font-medium leading-tight capitalize
                    ${isSelected ? 'text-orange-700' : 'text-gray-600'}
                `}>
                    {choice.label ?? choice.value}
                    
                </span>
                {note &&
                    <span className='text-2xs font-medium leading-tight capitalize text-gray-500'>{note}</span>
                }
            </div>
        </div>
    )
}

// List Selection Card Component (for longer text options)
const ListCard = ({ choice, config, isSelected, onClick }) => {
    const choiceConfig = config?.choiceIcons?.[choice.value] || {}
    const { emoji, color } = choiceConfig
    // choice.note: market note from utils/market-labels (the female premium in AED / RM)
    const note = choice.note ?? choiceConfig.note
    
    return (
        <div
            onClick={onClick}
            className={`
                relative p-3 rounded-xl cursor-pointer select-none
                transition-all duration-150 ease-out
                ${isSelected 
                    ? 'bg-linear-to-br from-orange-50 to-amber-50 border-2 border-orange-400' 
                    : 'bg-white border-2 border-gray-200 hover:border-orange-200'
                }
            `}
        >
            <div className="flex items-center gap-2">
                {/* Icon/Emoji */}
                <div className={`
                    shrink-0 rounded-lg flex items-center justify-center text-lg
                `}>
                    {emoji || "📌"}
                </div>
                
                {/* Text */}
                <span className={`
                    flex-1 font-medium
                    ${isSelected ? 'text-orange-700' : 'text-gray-700'}
                `}>
                    {choice.label ?? choice.value}
                </span>
                {note &&
                    <span className='text-xs font-medium leading-tight capitalize text-gray-500'>{note}</span>
                }
            </div>
        </div>
    )
}

// Open Text Input Component
const OpenTextInput = ({ value, onChange, placeholder }) => {
    return (
        <div className="w-full">
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder || "Please share any relevant information..."}
                rows={3}
                className="
                    w-full p-3 rounded-xl
                    text-gray-700 placeholder-gray-400 text-sm
                    bg-white border-2 border-gray-100
                    focus:border-orange-400 focus:ring-2 focus:ring-orange-100
                    transition-all duration-200 resize-none
                "
            />
            <p className="mt-1.5 text-xs text-gray-400 text-center">
                This helps trainers prepare for your sessions safely
            </p>
        </div>
    )
}

// Main Component
export const FitnessTrainerCriteria = (props) => {
    const { goToSection3, setRequestForm, requestCategorySettings } = props
    
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [selectedValue, setSelectedValue] = useState("")
    const [openTextValue, setOpenTextValue] = useState("")
    const [direction, setDirection] = useState(1)
    const [answers, setAnswers] = useState({})
    const [isInitialized, setIsInitialized] = useState(false)
    
    const { requestType, setRequestCriteria, setServiceItems, setHeadline, setServiceSummary } = useContext(RequestContext)
    // Customer's market: the account's country on /home, the page's on public pages.
    const { userCountryName } = useContext(UserContext) ?? {}
    const { countryName } = useContext(LocationContext) ?? {}
    const market = getMarketByName(userCountryName || countryName)
    
    // Get questions from requestCategorySettings (like ServiceCriteria.js)
    // Use the literal "Fitness Trainer" rather than the context requestType so the
    // questions resolve during SSR. The context requestType is only set client-side
    // (via useEffect), so relying on it would leave questions empty on the server and
    // render "Loading..." instead of the first question in the page source.
    const questions = useMemo(() => {
        if (!requestCategorySettings || requestCategorySettings.length === 0) return []
        
        for (let temp in requestCategorySettings) {
            if (requestCategorySettings[temp].requestType === "Fitness Trainer") {
                return requestCategorySettings[temp].criteriaQuestions || []
            }
        }
        return []
    }, [requestCategorySettings])

    
    const currentQuestion = questions[currentQuestionIndex]
    const config = currentQuestion ? questionConfig[currentQuestion.question_code] : null
    const isOpenText = currentQuestion?.inputType === "open"
    const isLastQuestion = currentQuestionIndex === questions.length - 1
    const firstQuestion = currentQuestionIndex === 0
    const useGrid = config?.useGrid && currentQuestion?.choices?.length >= 3
    
    // Initialize on mount - only once
    useEffect(() => {
        if (!isInitialized) {
            setRequestCriteria([])
            setServiceItems([])
            setServiceSummary("")
            setHeadline("")
            setIsInitialized(true)
        }
    }, [isInitialized, setRequestCriteria, setServiceItems, setServiceSummary, setHeadline])
    
    // Set default selection ONLY when question index changes
    useEffect(() => {
        if (!currentQuestion) return
        
        const isOpen = currentQuestion.inputType === "open"
        
        if (!isOpen && currentQuestion.choices?.length > 0) {
            const previousAnswer = answers[currentQuestion.question_code]
            if (previousAnswer) {
                setSelectedValue(previousAnswer)
            } else {
                setSelectedValue(currentQuestion.choices[0].value)
            }
        } else {
            setSelectedValue("")
            setOpenTextValue(answers[currentQuestion.question_code] || "")
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestionIndex]) // Only depend on currentQuestionIndex
    
    const handleSelectChoice = useCallback((choice) => {
        setSelectedValue(choice.value)
    }, [])
    
    const handleNext = useCallback(() => {
        if (!currentQuestion) return
        
        const value = isOpenText ? openTextValue : selectedValue
        
        const newAnswers = { ...answers, [currentQuestion.question_code]: value }
        setAnswers(newAnswers)
        
        if (value) {
            const chosen = currentQuestion.choices?.find((c) => c.value === value)
            const criteriaPhrase = criteriaSentence(currentQuestion.question_code, requestType, value, { market, label: chosen?.label })
            const requestCriteriaW = {
                criteria: criteriaPhrase,
                [currentQuestion.question_code]: value
            }
            setRequestCriteria(prev => {
                const filtered = prev.filter(c => !c[currentQuestion.question_code])
                return [...filtered, requestCriteriaW]
            })
        }
        
        if (isLastQuestion) {
            goToSection3()
            try {  
                //PT-Complete-Questions
                if(typeof gtag != "undefined") {
                    gtag('event', 'conversion', {'send_to': 'AW-823304067/g_G6CIPF2IUcEIO_yogD'});
                }
            } catch (error) {
                console.log(error)
            }
        } else {
            setDirection(1)
            setCurrentQuestionIndex(prev => prev + 1)
        }

        if(firstQuestion){     
            try {     
                //PT - Request Form Initiate
                if(typeof gtag != "undefined") {
                    gtag('event', 'conversion', {'send_to': 'AW-823304067/PKGQCPmeq5cbEIO_yogD'});
                }
            } catch (error) {
                console.log(error)
            }
        }
    }, [currentQuestion, isOpenText, openTextValue, selectedValue, answers, requestType, setRequestCriteria, isLastQuestion, goToSection3, firstQuestion, market])
    
    const handleBack = useCallback(() => {
        if (currentQuestionIndex === 0) {
            if (setRequestForm) {
                setRequestForm(false)
            } else {
                window.history.back()
            }
        } else {
            setDirection(-1)
            setCurrentQuestionIndex(prev => prev - 1)
        }
    }, [currentQuestionIndex, setRequestForm])
    
    const canProceed = isOpenText || selectedValue
    
    // Don't render if no questions
    if (!currentQuestion) {
        return <div className="text-center text-gray-500 py-8">Loading...</div>
    }
    
    return (
        <div className="max-w-lg mx-auto p-3 scrollbar-hide">
            {/* Question Content */}
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={currentQuestionIndex}
                    custom={direction}
                    variants={pageVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    {/* Compact Question Header */}
                    <div className="text-center mb-4">
                        {/* <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-2
                            bg-linear-to-br from-orange-500 to-amber-500 shadow-md">
                            {config?.icon && <config.icon className="w-6 h-6 text-white" />}
                        </div> */}
                        
                        <h3 className="text-sm font-bold text-gray-600 mb-3">
                            Find a Personal Trainer
                        </h3>                        
                        <p className="text-lg font-bold">
                            {config?.subtitle}
                        </p>
                    </div>
                    
                    {/* Choices */}
                    <div className="mb-4">
                        {isOpenText ? (
                            <OpenTextInput
                                value={openTextValue}
                                onChange={setOpenTextValue}
                                placeholder="E.g., lower back issue, knee surgery, asthma... or 'None'"
                            />
                        ) : useGrid ? (
                            <div className={`grid gap-2 ${
                                currentQuestion.choices.length <= 3 
                                    ? 'grid-cols-3' 
                                    : currentQuestion.choices.length <= 6 
                                        ? 'grid-cols-3' 
                                        : 'grid-cols-4'
                            }`}>
                                {currentQuestion.choices.map((choice) => (
                                    <CompactCard
                                        key={choice.value}
                                        choice={choice}
                                        config={config}
                                        isSelected={selectedValue === choice.value}
                                        onClick={() => handleSelectChoice(choice)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {currentQuestion.choices.map((choice) => (
                                    <ListCard
                                        key={choice.value}
                                        choice={choice}
                                        config={config}
                                        isSelected={selectedValue === choice.value}
                                        onClick={() => handleSelectChoice(choice)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
            
            {/* Navigation */}
            <div className="flex justify-between items-center pt-3 scrollbar-hide">
                <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-1 px-3 py-2 text-gray-500 hover:text-orange-600 
                        rounded-lg hover:bg-orange-50 transition-all duration-150 text-sm font-medium"
                >
                    <ChevronLeft className="w-5 h-5" />
                    {/* <span>Back</span> */}
                </button>
                
                <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canProceed && !isOpenText}
                    className={`
                        flex flex-col items-center ${firstQuestion ? ' px-8 py-4 ' : ' px-8 py-4 '} rounded-full font-semibold
                        transition-all duration-150 shadow-md
                        ${canProceed || isOpenText
                            ? 'bg-linear-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-500 text-white'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                        }
                    `}
                >
                    <span className="flex items-center gap-1.5 text-xl">
                        <HiSparkles/>
                        {isLastQuestion ? 'Next' : firstQuestion ? 'Next' : 'Next'}
                        <ChevronRight className="w-4 h-4" />
                    </span>
                    {/* {firstQuestion && (
                        <span className="text-xs font-normal opacity-80">less than 30 seconds</span>
                    )} */}
                </button>
            </div>

            {/* Progress Indicator */}
            {questions.length > 1 && (
                <div className="mt-3">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-gray-400">
                            Step {currentQuestionIndex + 1} of {questions.length}
                        </span>
                        <span className="text-xs text-gray-500">
                            Takes less than 30 seconds
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        {questions.map((_, i) => (
                            <div key={i} className="flex-1 flex items-center">
                                <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-gray-100">
                                    <motion.div
                                        className="h-full rounded-full bg-linear-to-r from-orange-500 to-amber-400"
                                        initial={false}
                                        animate={{ 
                                            width: i < currentQuestionIndex ? '100%' : i === currentQuestionIndex ? '50%' : '0%'
                                        }}
                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Company Trust Badge */}
            {/* <div className="mt-2 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-center gap-1 text-gray-400">
                    <p className="text-xs">
                        StringsSG Pte Ltd &middot; UEN 201813375G
                    </p>
                </div>
            </div> */}
        </div>
    )
}